import type {
	BooleanFilterDeps,
	BooleanFilterState,
	BooleanFilterType,
	BooleanFilterTypeFunc,
	CreateBooleanFilter,
	CreatedBooleanFilter,
} from '../../../common/constants/filters/boolean-filter/types';
import { type CloudConfig } from '../../../common/constants/filters/cloud-config/types';

/** Returns a getState accessor for a boolean filter's query parameters. */
export const generateQueryParamsGetState = <ID extends string>(
	initialState: BooleanFilterState<ID>,
	queryParamKey: string,
) => {
	return (queryParams: Record<string, string[]>): BooleanFilterState<ID> => {
		const queryParamValue = queryParams[queryParamKey];
		const booleanValue = queryParamValue?.[0] === 'true' ? true : false;
		return {
			...initialState,
			value: booleanValue || initialState.value,
		};
	};
};

/**
 * Factory that creates a boolean filter definition from a filter ID and configuration.
 *
 * Returns a BooleanFilter object with getState and generateQueryParams methods
 * that read/write boolean filter state without depending on React hooks.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const createBooleanFilter: <ID extends string>(
	initial: CreateBooleanFilter<ID>,
) => CreatedBooleanFilter<ID> = <ID extends string>(
	initial: CreateBooleanFilter<ID>,
): CreatedBooleanFilter<ID> => {
	const initialState = {
		...initial,
		type: 'boolean',
		value: false,
		hasSelection: false,
		universal: initial.universal,
	} satisfies BooleanFilterState<ID>;

	return {
		id: initial.id,
		type: initialState.type,
		products: initial.products,
		universal: initial.universal,
		queryParams: {
			key: initialState.queryParams.key,
			getState: (initial.queryParams.generateQueryParamsGetState ?? generateQueryParamsGetState)(
				initialState,
				initial.queryParams.key,
			),
		},
		customValueFields: initial.customValueFields,
		withState: (
			state: unknown, // To support polymorphic function calls from the Store
			config: CloudConfig,
		) =>
			BooleanFilter({
				initial,
				state: state as BooleanFilterState<ID>, // This has to be cast back to the correct state
				config,
			}),
		getInitialState: (config: CloudConfig) =>
			BooleanFilter({ initial, state: initialState, config }),
		isCompatible: (id) => id === initial.id,
		isSupportCustomValue: false,
		isSuggestedFilter: initial.isSuggestedFilter,
	};
};

/**
 * Composes a boolean filter from its initial definition, current state, and config.
 *
 * Wires up getState and generateQueryParams for the filter pipeline without
 * requiring React — the hook wrapper (useBooleanFilter) is separate.
 */
export const BooleanFilter: BooleanFilterType = ({ initial, state, config }) => {
	const filterState = {
		...state,
		hasSelection: state.value,
	};

	return {
		...filterState,
		setValue: setValue({ initial, state: filterState, config }),
		getValue: () => filterState.value,
		getState: () => filterState,
		queryParams: {
			key: initial.queryParams.key,
			get: getQueryParams({ initial, state: filterState, config }),
			set: setQueryParams({ initial, state: filterState, config }),
		},
		clear: clear({ initial, state: filterState, config }),
	};
};

const setValue = <ID extends string>({ initial, state, config }: BooleanFilterDeps<ID>) => {
	return (value: boolean) => {
		const newState = {
			...state,
			value,
			hasSelection: value,
		} satisfies BooleanFilterState<ID>;

		return BooleanFilter({ initial, state: newState, config });
	};
};

const getQueryParams = <ID extends string>({ initial, state }: BooleanFilterDeps<ID>) => {
	return () => ({
		[initial.queryParams.key]: state.value === true ? ['true'] : undefined,
	});
};

const setQueryParams = <ID extends string>({ initial, state, config }: BooleanFilterDeps<ID>) => {
	return async (
		queryParams: Record<string, string[] | undefined>,
	): Promise<ReturnType<BooleanFilterTypeFunc<ID>>> => {
		const queryParamValue = queryParams[initial.queryParams.key];
		const booleanValue = queryParamValue?.[0] === 'true' ? true : false;

		return BooleanFilter({
			initial,
			state: { ...state, value: booleanValue, hasSelection: booleanValue },
			config,
		});
	};
};

const clear = <ID extends string>({ initial, state, config }: BooleanFilterDeps<ID>) => {
	return () => {
		const filter = BooleanFilter({ initial, state, config });
		return filter.setValue(false);
	};
};
